import React from 'react';
import PropTypes from 'prop-types';
import { ValidatedTextInput } from '@els/els-react--text-input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { PASSWORD_ERRORS, validateConfirmationPassword, validatePassword } from '@Utils/Password';
import { RESPONSE_STATUS } from '@Pages/Login/ResetPasswordWithTokenDialog/ResetPasswordWithTokenDialogConstants';
import ResetPasswordWithTokenDialogError from './ResetPasswordWithTokenDialogError';

const spinnerId = 'resetPasswordFormToken';

const errorsMessages = {
  [PASSWORD_ERRORS.REQUIRED]: 'Please choose a new password',
  [PASSWORD_ERRORS.LOWER_CASE]: 'Password must have at least one lower case character',
  [PASSWORD_ERRORS.UPPER_CASE]: 'Password must have at least one upper case character',
  [PASSWORD_ERRORS.DIGIT_OR_SPECIAL]: 'Password must have at least one numeric character or special characters',
  [PASSWORD_ERRORS.MIN_LENGTH]: 'Password must have minimum of 8 characters',
  [PASSWORD_ERRORS.MAX_LENGTH]: 'Password must have maximum of 200 characters',
  [PASSWORD_ERRORS.RESTRICT]: 'Password must not contain prohibited expression',
  [PASSWORD_ERRORS.SEQUENCE]: 'Password must not contain sequential alpha numeric characters',
  [PASSWORD_ERRORS.CHECK_ASCII]: 'Password must contain only printable, non-whitespace ASCII characters',
  [PASSWORD_ERRORS.COMPARE_TO]: 'The two passwords given are not the same. Please check your spelling',
  [PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]: 'Please confirm new password',
};

function ResetPasswordWithTokenDialogContent({
  trackAdobeAnalyticsError,
  responseStatus,
  newPassword,
  setNewPassword,
  confirmationPassword,
  setConfirmationPassword,
  requestSent,
  openForgotPasswordDialog,
  errorDetails,
}) {
  const handleNewPasswordChange = (value) => {
    const { isValid, errors } = validatePassword(value);
    setNewPassword({
      value,
      isValid,
      errors,
      dirty: true,
    });
    const confirmationPasswordValidation = validateConfirmationPassword(value, confirmationPassword.value);
    setConfirmationPassword({ ...confirmationPassword, ...confirmationPasswordValidation });
  };

  const handleConfirmationPasswordChange = (value) => {
    const { isValid, errors } = validateConfirmationPassword(newPassword.value, value);
    setConfirmationPassword({
      value,
      isValid,
      errors,
      dirty: true,
    });
  };

  const trackNewPasswordFormError = () => {
    trackAdobeAnalyticsError(newPassword.errors, 'fe-');
  };

  const trackConfirmationPasswordFormError = () => {
    trackAdobeAnalyticsError(confirmationPassword.errors, 'fe-');
  };

  const getPasswordValidationErrors = (password) => {
    if (!password.dirty || password.isValid) {
      return null;
    }
    const errors = password.errors
      .filter((error) => requestSent || (error !== PASSWORD_ERRORS.REQUIRED && error !== PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED))
      .map((error) => ({ isValid: false, message: errorsMessages[error] }));
    return errors.length > 0 ? errors : null;
  };

  const getNewPasswordValidationErrors = () => {
    if (requestSent && !newPassword.dirty) {
      return [{ isValid: false, message: errorsMessages[PASSWORD_ERRORS.REQUIRED] }];
    }
    return getPasswordValidationErrors(newPassword);
  };

  const getConfirmationPasswordValidationErrors = () => {
    if (requestSent && !confirmationPassword.dirty) {
      return [{ isValid: false, message: errorsMessages[PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED] }];
    }
    return getPasswordValidationErrors(confirmationPassword);
  };
  const isLoading = responseStatus === RESPONSE_STATUS.LOADING;
  const isSuccess = responseStatus === RESPONSE_STATUS.SUCCESS;
  const isError = responseStatus === RESPONSE_STATUS.ERROR;

  return (
    <>
      {isLoading && <SimpleSpinner id={spinnerId} />}
      {isSuccess && <div className="reset-password-dialog__result">Password reset successfully.</div>}
      {isError && <ResetPasswordWithTokenDialogError openForgotPasswordDialog={openForgotPasswordDialog} errorDetails={errorDetails} />}
      {!isSuccess && !isError && (
        <form onSubmit={(e) => e.preventDefault()}>
          <ValidatedTextInput
            data-e2e="reset-password-new-password"
            id="reset-password-new-password"
            className="e2e-forgot-password-email-input u-els-padding-top-1x1o2 u-els-padding-bottom"
            label="New password"
            value={newPassword.value}
            onChange={(e) => handleNewPasswordChange(e?.target?.value)}
            onBlur={trackNewPasswordFormError}
            disabled={isLoading}
            required
            hideRequired
            validator={getNewPasswordValidationErrors}
            onlyValidateWhenTouched
            type="password"
            name="new-password"
            autoComplete="new-password"
          />
          <ValidatedTextInput
            data-e2e="reset-password-confirmation-password"
            id="reset-password-confirmation-password"
            className="e2e-forgot-password-email-input u-els-padding-bottom"
            label="Confirm password"
            value={confirmationPassword.value}
            onChange={(e) => handleConfirmationPasswordChange(e?.target?.value)}
            onBlur={trackConfirmationPasswordFormError}
            disabled={isLoading}
            required
            hideRequired
            validator={getConfirmationPasswordValidationErrors}
            onlyValidateWhenTouched
            type="password"
            name="confirm-password"
            autoComplete="new-password"
          />
        </form>
      )}
    </>
  );
}

ResetPasswordWithTokenDialogContent.propTypes = {
  trackAdobeAnalyticsError: PropTypes.func.isRequired,
  responseStatus: PropTypes.string,
  newPassword: PropTypes.object.isRequired,
  setNewPassword: PropTypes.func.isRequired,
  confirmationPassword: PropTypes.object.isRequired,
  openForgotPasswordDialog: PropTypes.func,
  errorDetails: PropTypes.string,
  setConfirmationPassword: PropTypes.func.isRequired,
  requestSent: PropTypes.bool,
};

export default ResetPasswordWithTokenDialogContent;
